import axios from 'axios';
import qs from 'qs';
import Cookies from 'js-cookie';
import {setTokens, setTokenRefreshing, setTokenExpiry} from '../graphql/localState';

// Basic auth credentials for OAuth endpoint
const OAUTH_CLIENT_ID = 'EoYCm7LFks0aSI5RCMiNjCvOEehG5EEC';
const OAUTH_CLIENT_SECRET = 'YaFAERl0fJX2Pfl7Qd6m';

// Encode credentials for Basic Auth header
const getBasicAuthHeader = () => {
  const credentials = `${OAUTH_CLIENT_ID}:${OAUTH_CLIENT_SECRET}`;
  const encoded = btoa(credentials);
  return `Basic ${encoded}`;
};

// Parse JWT helper
const parseJwt = (token: string) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch (e) {
    return null;
  }
};

// Get expiry date from token
const getExpiry = (token: string) => {
  if (token == null) return new Date();
  const payload = parseJwt(token);
  return payload?.exp ? new Date(payload.exp * 1000) : new Date();
};

// Get cookie domain from environment
const getCookieDomain = () => {
  return import.meta.env.VITE_COOKIE_DOMAIN || 'localhost';
};

export const LOGIN_PAGE_PATH = '/admin/auth/login-page';

const TOKEN_ENDPOINT = '/oauth/token';

// Forget the session everywhere the app keeps it: localStorage, the cookies the WebSocket
// authenticates with, and the axios default header.
export const clearAuthState = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('selected_organisation_id');
  Cookies.remove('main_token', {path: '/', domain: getCookieDomain()});
  Cookies.remove('main_refresh_token', {path: '/', domain: getCookieDomain()});
  delete axios.defaults.headers.common['Authorization'];
};

// A full page load rather than a router navigation: it drops the Apollo cache, every
// component's state and the WebSocket, so nothing can go on showing a logged-in admin UI.
export const redirectToLogin = () => {
  window.location.href = LOGIN_PAGE_PATH;
};

// JWT token expiry checker
const isTokenExpiringSoon = (token: string, bufferMinutes: number = 5): boolean => {
  try {
    const payload = parseJwt(token);

    if (!payload?.exp) {
      console.warn('[TokenRefresh] Token has no expiry claim');
      return true; // Assume expired if no exp claim
    }

    const expiryTime = payload.exp * 1000; // Convert to milliseconds
    const currentTime = Date.now();
    const bufferTime = bufferMinutes * 60 * 1000;

    return expiryTime - currentTime <= bufferTime;
  } catch (error) {
    console.error('[TokenRefresh] Failed to decode token:', error);
    return true; // Assume expired if we can't decode
  }
};

const performTokenRefresh = async (): Promise<boolean> => {
  const refreshToken = localStorage.getItem('refresh_token');

  if (!refreshToken) {
    console.warn('[TokenRefresh] No refresh token available');
    return false;
  }

  try {
    console.log('[TokenRefresh] Refreshing access token...');
    setTokenRefreshing(true);

    // cway-server reads the refresh token from a `Refresh-Token` header, a `refresh_token`
    // cookie or a JSON body - never from a form field, so a form-encoded token is simply not
    // seen and the refresh is refused. It also ignores `organisation_id` on this grant: the new
    // token keeps the organisation the refresh token carries.
    const response = await axios.post(TOKEN_ENDPOINT, qs.stringify({grant_type: 'refresh_token'}), {
      headers: {
        Authorization: getBasicAuthHeader(),
        'Content-Type': 'application/x-www-form-urlencoded',
        'Refresh-Token': refreshToken,
      },
      withCredentials: true,
    });

    if (response.status === 200 && response.data?.access_token) {
      const {access_token, refresh_token: new_refresh_token} = response.data;

      // Update tokens in localStorage
      localStorage.setItem('access_token', access_token);
      if (new_refresh_token) {
        localStorage.setItem('refresh_token', new_refresh_token);
      }

      // Update cookies
      Cookies.set('main_token', access_token, {
        path: '/',
        domain: getCookieDomain(),
        expires: getExpiry(access_token),
      });
      if (new_refresh_token) {
        Cookies.set('main_refresh_token', new_refresh_token, {
          path: '/',
          domain: getCookieDomain(),
          expires: getExpiry(new_refresh_token),
        });
      }

      // Update Authorization header for all future requests
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      // Update Apollo cache with new tokens
      const expiry = getTokenExpiryTime(access_token);
      setTokens(access_token, new_refresh_token || refreshToken, expiry);

      console.log('[TokenRefresh] Token refreshed successfully');
      setTokenRefreshing(false);

      return true;
    }

    return false;
  } catch (error: any) {
    console.error('[TokenRefresh] Failed to refresh token:', error.message);
    setTokenRefreshing(false);

    // The refresh token was refused: the session is over, so clear everything and leave for
    // the login page. cway-server means to answer 401 here, but the token endpoint's refusals
    // reach the browser as 403 - a missing, expired or forged refresh token all come back 403.
    const status = error.response?.status;
    if (status === 400 || status === 401 || status === 403) {
      console.warn(`[TokenRefresh] Refresh token refused (${status}), clearing auth state and redirecting to login`);
      clearAuthState();
      redirectToLogin();
    }

    return false;
  }
};

// Refresh the access token using the refresh token. Callers that overlap (the refresh
// interval, the expiry check, the 401 interceptor) share one request: a second concurrent
// refresh would race the first, and with a rotated refresh token could fail outright.
let refreshInFlight: Promise<boolean> | null = null;

export const refreshAccessToken = (): Promise<boolean> => {
  if (!refreshInFlight) {
    refreshInFlight = performTokenRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
};

// Check if token needs refresh and refresh if necessary
export const checkAndRefreshToken = async (): Promise<boolean> => {
  const accessToken = localStorage.getItem('access_token');

  if (!accessToken) {
    console.log('[TokenRefresh] No access token, skipping refresh check');
    return false;
  }

  if (isTokenExpiringSoon(accessToken)) {
    console.log('[TokenRefresh] Token expiring soon, refreshing...');
    return await refreshAccessToken();
  }

  console.log('[TokenRefresh] Token still valid');
  return true;
};

// Set up timer to update Apollo cache with current token state every second
let stateUpdateInterval: NodeJS.Timeout | null = null;

// The refresh interval renews the token five minutes before it expires, so reaching the
// expiry means that did not happen: the machine slept past it, the network was down, or the
// refresh failed with a status other than 401/400 (those redirect on their own). Left alone,
// the page keeps showing the admin UI with a token the server rejects. Try once more, and if
// that fails leave for the login page.
let expiredTokenHandling: Promise<void> | null = null;

const handleExpiredToken = () => {
  if (expiredTokenHandling) return;

  console.warn('[TokenRefresh] Access token has expired, making a final refresh attempt');
  expiredTokenHandling = refreshAccessToken()
    .catch(() => false)
    .then((refreshed) => {
      if (refreshed) {
        console.log('[TokenRefresh] Expired token replaced');
        return;
      }
      console.warn('[TokenRefresh] Could not replace the expired token, leaving for the login page');
      stopTokenRefreshInterval();
      clearAuthState();
      redirectToLogin();
    })
    .finally(() => {
      expiredTokenHandling = null;
    });
};

const updateTokenState = () => {
  const accessToken = localStorage.getItem('access_token');
  if (!accessToken) return;

  const expiry = getTokenExpiryTime(accessToken);
  setTokenExpiry(expiry);

  if (expiry !== null && expiry <= Date.now()) {
    handleExpiredToken();
  }
};

const startTokenStateUpdates = () => {
  if (stateUpdateInterval) return;

  console.log('[TokenRefresh] Starting token state updates');
  updateTokenState();
  stateUpdateInterval = setInterval(updateTokenState, 1000); // Update every second
};

const stopTokenStateUpdates = () => {
  if (stateUpdateInterval) {
    clearInterval(stateUpdateInterval);
    stateUpdateInterval = null;
  }
};

// Set up automatic token refresh interval (check every minute)
let refreshInterval: NodeJS.Timeout | null = null;

export const startTokenRefreshInterval = () => {
  if (refreshInterval) {
    console.log('[TokenRefresh] Refresh interval already running');
    return;
  }

  console.log('[TokenRefresh] Starting automatic token refresh interval');

  // Initialize Apollo cache with current tokens
  const accessToken = localStorage.getItem('access_token');
  const refreshToken = localStorage.getItem('refresh_token');
  if (accessToken) {
    const expiry = getTokenExpiryTime(accessToken);
    setTokens(accessToken, refreshToken, expiry);
  }

  // Start state updates
  startTokenStateUpdates();

  // Check immediately on start
  checkAndRefreshToken();

  // Check every minute
  refreshInterval = setInterval(() => {
    checkAndRefreshToken();
  }, 60 * 1000); // 60 seconds
};

export const stopTokenRefreshInterval = () => {
  if (refreshInterval) {
    console.log('[TokenRefresh] Stopping token refresh interval');
    clearInterval(refreshInterval);
    refreshInterval = null;
  }

  stopTokenStateUpdates();

  // Clear tokens from Apollo cache
  setTokens(null, null, null);
};

// Parse JWT and extract expiry
const getTokenExpiryTime = (token: string): number | null => {
  try {
    const payload = parseJwt(token);
    return payload?.exp ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
};

// Axios interceptor to handle 401 errors and retry with refreshed token
let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

const subscribeTokenRefresh = (callback: (token: string) => void) => {
  refreshSubscribers.push(callback);
};

const onTokenRefreshed = (token: string) => {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
};

export const setupAxiosInterceptors = () => {
  // Response interceptor to handle 401 errors
  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      // A 401 from the token endpoint is a failed login or a failed refresh, and the code that
      // made the request handles it. Answering it with a refresh would make the refresh wait on
      // itself: refreshAccessToken() hands back the very request that is waiting here, it never
      // settles, and from then on every refresh and the expiry check wait on it too, so the
      // page never leaves for the login page.
      if (originalRequest?.url === TOKEN_ENDPOINT) {
        return Promise.reject(error);
      }

      // If error is 401 and we haven't tried to refresh yet
      if (error.response?.status === 401 && !originalRequest._retry) {
        if (isRefreshing) {
          // If already refreshing, queue this request
          return new Promise((resolve) => {
            subscribeTokenRefresh((token: string) => {
              originalRequest.headers['Authorization'] = `Bearer ${token}`;
              resolve(axios(originalRequest));
            });
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const refreshed = await refreshAccessToken();

          if (refreshed) {
            const newToken = localStorage.getItem('access_token');
            if (newToken) {
              isRefreshing = false;
              onTokenRefreshed(newToken);

              // Retry the original request with new token
              originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
              return axios(originalRequest);
            }
          }

          // If refresh failed, redirect to login
          isRefreshing = false;
          console.warn('[TokenRefresh] Token refresh failed, redirecting to login');
          redirectToLogin();
          return Promise.reject(error);
        } catch (refreshError) {
          isRefreshing = false;
          console.warn('[TokenRefresh] Token refresh error, redirecting to login');
          redirectToLogin();
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    },
  );

  console.log('[TokenRefresh] Axios interceptors configured');
};
